import { csvCell } from '../reports/reports';
import { softwareRevisionText, type SoftwareRevision } from './importMatchLog';
import type { ImportBlockValue } from './importRun';
import type { ImportStagedKindValue } from './importStaging';

/**
 * 🔒 The Compare Reports download (piece 31; decision 13070, keeping 5981; row T3894): one line per record and field of a
 * run, laying the value the read parsed beside the value written here, with the difference marked. The route answers the
 * lines, and the page makes the one CSV from them here, so the file's form lives in one place.
 *
 * 🔨 It carries incoming values by its nature, so it is never the match log and Copy for Claude never pastes it. Every cell
 * goes through the reports' CSV cell, which writes a cell that opens like a formula as text.
 */

/** 🔒 The five words the Difference column reads. */
export const COMPARE_DIFFERENCES = ['Same', 'Differs', 'Missing Here', 'Missing There', 'Not Mapped'] as const;
export type CompareDifference = (typeof COMPARE_DIFFERENCES)[number];

/**
 * 🔒 The blocks the compare reads (decision 13070): the Zoho Expense reports and the Zoho Books documents. A block joins by
 * a reader of its own on the server and its name here; the page draws the control on a run of a block named here alone.
 */
export const COMPARE_REPORT_BLOCKS: readonly ImportBlockValue[] = ['ExpenseReports', 'Documents'];

export function compareReportsReads(block: string): boolean {
  return (COMPARE_REPORT_BLOCKS as readonly string[]).includes(block);
}

/** 🔒 The route's one-record parameter: the staged row whose record the file holds alone. */
export const COMPARE_REPORTS_ROW_PARAM = 'row';

/** 🔨 Relative to the API's own root, never prefixed with /api (piece 1). */
export function importRunCompareReportsPath(runId: string, rowId: string | null = null): string {
  const base = `/import/runs/${encodeURIComponent(runId)}/compare-reports`;
  return rowId ? `${base}?${COMPARE_REPORTS_ROW_PARAM}=${encodeURIComponent(rowId)}` : base;
}

/** One line of the file: one field of one record, or of one of its lines. */
export interface CompareLine {
  sequence: number;
  sourceKind: ImportStagedKindValue;
  sourceId: string;
  /** The record as the read names it, the staged row's own label. */
  record: string;
  /** The record written here by its number or name, empty where none was written. */
  recordHere: string | null;
  /** Empty on a record's own field; a line's place in the source, or `here N` for a line here no source line pairs with. */
  line: string;
  field: string;
  sourceValue: string;
  valueHere: string;
  difference: CompareDifference;
}

/** What the route answers. */
export interface CompareReportsAnswer {
  runId: string;
  /** The run in a line: its service, block, day and range. */
  runLabel: string;
  block: ImportBlockValue;
  /** The one record chosen, its sequence and its label, or null for the whole run. */
  record: { sequence: number; label: string } | null;
  environment: string;
  revision: SoftwareRevision;
  startedBeforeRevision: boolean;
  exportedAt: string;
  lines: CompareLine[];
}

/**
 * 🔒 One field's difference (decision 13070). Two empty sides read Same; one empty side reads Missing on that side; two
 * values read Same where the field's own test says so, or where their trimmed text matches, and Differs otherwise.
 */
export function compareDifference(source: string | null, here: string | null, same?: boolean): CompareDifference {
  const there = source === null ? '' : source.trim();
  const ours = here === null ? '' : here.trim();
  if (there === '' && ours === '') return 'Same';
  if (ours === '') return 'Missing Here';
  if (there === '') return 'Missing There';
  return (same ?? there === ours) ? 'Same' : 'Differs';
}

/** The file's columns, in order: each a heading and what the line gives under it. */
const COLUMNS: readonly { heading: string; of: (line: CompareLine) => string }[] = [
  { heading: 'Sequence', of: (line) => String(line.sequence) },
  { heading: 'Source Kind', of: (line) => line.sourceKind },
  { heading: 'Source Id', of: (line) => line.sourceId },
  { heading: 'Record', of: (line) => line.record },
  { heading: 'Record Here', of: (line) => line.recordHere ?? '' },
  { heading: 'Line', of: (line) => line.line },
  { heading: 'Field', of: (line) => line.field },
  { heading: 'Source Value', of: (line) => line.sourceValue },
  { heading: 'Value Here', of: (line) => line.valueHere },
  { heading: 'Difference', of: (line) => line.difference },
];

/** 🔒 The file's first line (decision 7910's form): the run, its block, the record where one is chosen, the environment, the revision and the time. */
export function compareReportsHeadLine(answer: Omit<CompareReportsAnswer, 'lines'>): string {
  const record = answer.record ? `; record ${answer.record.sequence} (${answer.record.label})` : '; every record';
  const earlier = answer.startedBeforeRevision ? '; the run started before this revision was deployed, under an earlier one' : '';
  return `Compare Reports of run ${answer.runId.slice(0, 8)} (${answer.runLabel}); block ${answer.block}${record}; environment ${answer.environment}; software revision ${softwareRevisionText(answer.revision)}${earlier}; exported ${answer.exportedAt}`;
}

/** 🔒 The file: the first line, the heading line, then one line per record and field, every cell through the reports' guarded CSV cell. */
export function compareReportsCsv(answer: CompareReportsAnswer): string {
  const row = (cells: readonly string[]) => cells.map((cell) => csvCell(cell)).join(',');
  return [csvCell(compareReportsHeadLine(answer)), row(COLUMNS.map((column) => column.heading)), ...answer.lines.map((line) => row(COLUMNS.map((column) => column.of(line))))].join('\r\n') + '\r\n';
}

/** 🔒 The file's name: the run's first eight characters, the record's sequence where one is chosen, then the viewer's day, as the outside calls export names its file. */
export function compareReportsFileName(runId: string, sequence: number | null, day: string): string {
  return ['compare-reports', `run-${runId.slice(0, 8)}`, sequence === null ? '' : `row-${sequence}`, day].filter((one) => one !== '').join('-') + '.csv';
}
